/**
 * The planning loop's shared pieces (wave W2): the date check, the date-move dialog and
 * the "whose turn is it" line, used by the schedule, the activity and the calendar.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Every dialog here keeps its refusal INSIDE itself (ActionDialog's `error`) and stays
 * open until the server says yes. Every act it offers came from the server's own list of
 * what the caller may do now (`availableMoves`), never from a guess on this side.
 */

import { useState } from "react";

import { ApiError } from "../api/client";
import {
  previewConflicts,
  recordNegotiationMove,
  type Activity,
  type CalendarConflict,
  type NegotiationMove,
} from "../api/schedules";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog } from "../nav/ActionDialog";
import { day } from "./parts";

/* ------------------------------------------------------------------ */
/* Check the date: a real answer, inside the form.                      */
/* ------------------------------------------------------------------ */

/**
 * Mahmoud pressed "Check the date" and nothing happened: the preview was sent without
 * its project, refused, and the refusal swallowed. The check now asks with the project
 * and says one of three things in plain words: clear, a caution period (allowed, and
 * every approver sees it), or a blocked period (the write will be refused).
 */
export function DateCheck({
  projectId,
  plannedDate,
  plannedEndDate,
}: {
  readonly projectId: string | null;
  readonly plannedDate: string;
  readonly plannedEndDate?: string | null;
}): React.JSX.Element {
  const token = useToken();
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState<CalendarConflict[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const ready = projectId !== null && plannedDate.length > 0;

  async function check(): Promise<void> {
    if (!ready) {
      return;
    }
    setBusy(true);
    setFailed(null);
    try {
      setVerdict(
        await previewConflicts(token, {
          projectId,
          plannedDate,
          plannedEndDate: plannedEndDate ?? null,
        }),
      );
    } catch (error) {
      setVerdict(null);
      setFailed(error instanceof ApiError ? error.message : strings.common.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="q-field" data-testid="date-check">
      <button
        type="button"
        className="q-button q-button--quiet"
        data-testid="date-check-button"
        disabled={busy || !ready}
        title={ready ? undefined : strings.schedules.checkDateNeedsDate}
        onClick={() => {
          void check();
        }}
      >
        {strings.schedules.previewConflicts}
      </button>
      <div role="status" aria-live="polite" data-testid="date-check-verdict">
        {failed !== null && <p className="q-field__error">{failed}</p>}
        {verdict !== null && verdict.length === 0 && (
          <p className="q-note">{strings.schedules.checkDateVerdictClear}</p>
        )}
        {verdict?.map((entry, index) => (
          <p
            key={index}
            className={entry.enforcement === "BLOCK" ? "q-field__error" : "q-note"}
            data-testid={
              entry.enforcement === "BLOCK" ? "date-check-blocked" : "date-check-caution"
            }
          >
            {entry.enforcement === "BLOCK"
              ? strings.schedules.checkDateVerdictBlocked(
                  entry.periodName,
                  day(entry.startsOn),
                  day(entry.endsOn),
                )
              : strings.schedules.checkDateVerdictCaution(
                  entry.periodName,
                  day(entry.startsOn),
                  day(entry.endsOn),
                )}
          </p>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Whose turn is it                                                     */
/* ------------------------------------------------------------------ */

export function NegotiationLine({ activity }: { readonly activity: Activity }): React.JSX.Element {
  return (
    <span data-testid="negotiation-state">
      {strings.schedules.negotiation[activity.negotiationState] ?? activity.negotiationState}
      {activity.pendingDate !== null && (
        <span className="q-note">
          {" "}
          {strings.schedules.pendingDateNote(day(activity.pendingDate))}
        </span>
      )}
    </span>
  );
}

/** The label of one move as THIS activity's state reads it. */
export function moveLabel(move: NegotiationMove, activity: Activity): string {
  switch (move) {
    case "ACKNOWLEDGE":
      return activity.pendingDate === null
        ? strings.schedules.acknowledge
        : strings.schedules.acceptDate(day(activity.pendingDate));
    case "PROPOSE":
      return strings.schedules.counterPropose;
    case "CONFIRM":
      return strings.schedules.agreeDate;
    case "REJECT":
      return strings.schedules.keepPlannedDate;
  }
}

/* ------------------------------------------------------------------ */
/* One move on a planned date                                           */
/* ------------------------------------------------------------------ */

export function DateMoveDialog({
  activity,
  move,
  onClose,
  onDone,
}: {
  readonly activity: Activity;
  readonly move: NegotiationMove;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [proposedDate, setProposedDate] = useState("");
  const [justification, setJustification] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const label = moveLabel(move, activity);
  const needsDate = move === "PROPOSE";
  const needsReason = move === "PROPOSE" || move === "REJECT";
  const ready =
    (!needsDate || proposedDate.length > 0) && (!needsReason || justification.trim().length > 0);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await recordNegotiationMove(token, activity.id, {
        move,
        // A confirmation agrees the date on the table, which the server names; the
        // client sends it as the server's own pending date, never a typed one.
        proposedDate:
          move === "PROPOSE" ? proposedDate : move === "CONFIRM" ? activity.pendingDate : null,
        justification: needsReason ? justification.trim() : null,
      });
      onDone(strings.schedules.moveDone[move] ?? strings.schedules.actionToastDone(label));
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={`${activity.humanId}: ${label}`} onClose={onClose} error={error}>
      <p className="q-shell__lede" data-testid="date-move-lede">
        {strings.schedules.columnDate}: {day(activity.plannedDate)}.{" "}
        <NegotiationLine activity={activity} />
      </p>
      {needsDate && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="date-move-date">
            {strings.schedules.proposedDateLabel}
          </label>
          <ServerField field="proposed_date">
            <input
              id="date-move-date"
              className="q-input"
              type="date"
              data-testid="proposed-date-input"
              value={proposedDate}
              onChange={(event) => {
                setProposedDate(event.target.value);
              }}
            />
          </ServerField>
        </div>
      )}
      {needsReason && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="date-move-reason">
            {move === "REJECT"
              ? strings.schedules.keepReasonLabel
              : strings.schedules.justificationLabel}
          </label>
          <ServerField field="justification">
            <textarea
              id="date-move-reason"
              className="q-textarea"
              data-testid="date-move-reason"
              value={justification}
              onChange={(event) => {
                setJustification(event.target.value);
              }}
            />
          </ServerField>
        </div>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="dialog-confirm"
          disabled={busy || !ready}
          onClick={() => {
            void send();
          }}
        >
          {label}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
